import { useState } from "react";
import { GoalDraftForm } from "./goals/GoalDraftForm";
import { GoalPicker } from "./goals/GoalPicker";
import { descendantsOf, notifyGoalsChanged, useWorldGoals } from "../neucleos/useWorldGoals";
import { mintWorldSubGoal } from "../neucleos/worldSessionClient";
import { goalContextView, useGoalContextView } from "../neucleos/goalContextView";
import { randomUUID } from "../lib/utils";
import { Dialog, DialogPopup, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
export function GoalAssignment({
  worldKey,
  roomGoalIds,
  value,
  onChange,
  disabled = false,
}: {
  worldKey: string;
  roomGoalIds: ReadonlyArray<string>;
  value: ReadonlyArray<string>;
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}) {
  const data = useWorldGoals(worldKey),
    [creating, setCreating] = useState(false);
  const setView = useGoalContextView((s) => s.setView);
  const selectedGoals = data.goals.filter((g) => value.includes(g.goalId));
  const contexts = selectedGoals.map((g) => goalContextView(worldKey, g, data.goals));
  const allowed = descendantsOf(data.goals, roomGoalIds),
    goals = data.goals.filter((g) => allowed.has(g.goalId));
  const parent = goals.find((g) => g.goalId === (value[0] ?? roomGoalIds[0]));
  return (
    <div className="space-y-2" data-goal-assignment>
      <p className="text-xs font-medium">Goals for this session</p>
      {data.loading ? (
        <p className="text-xs text-muted-foreground">Reading room goals…</p>
      ) : (
        <GoalPicker goals={goals} value={value} onChange={onChange} disabled={disabled} />
      )}
      {data.error ? (
        <p role="alert" className="text-xs text-destructive">
          {data.error}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button
          size="sm"
          type="button"
          variant="outline"
          disabled={disabled || !parent}
          onClick={() => setCreating(true)}
        >
          Create subgoal
        </Button>
        <Button
          size="sm"
          type="button"
          variant="ghost"
          disabled={disabled}
          onClick={() => onChange([])}
        >
          No goal for now
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Choose from this room’s goals and descendants. Review this session’s context in the side
        rail before sending.
      </p>
      {contexts.some((context) => context.links.length) ? (
        <div
          className="space-y-2 rounded-md border border-border p-2 text-xs"
          aria-label="Suggested goal context"
        >
          <p className="font-medium">Relevant context</p>
          {contexts.map((context) =>
            context.links.length ? (
              <div key={context.goalId}>
                <button type="button" className="underline" onClick={() => setView(context)}>
                  {context.title} · show in side rail
                </button>
                {context.links.map((link) => (
                  <p
                    key={`${link.memberId}-${link.inheritedGoalId ?? context.goalId}`}
                    className="break-all text-muted-foreground"
                  >
                    {link.memberId} · {link.reason}
                    {link.inheritedFrom ? ` · from ${link.inheritedFrom}` : ""}
                  </p>
                ))}
              </div>
            ) : null,
          )}
        </div>
      ) : null}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogPopup className="w-[420px] max-w-[90vw]">
          <DialogTitle>Create subgoal</DialogTitle>
          {parent ? (
            <GoalDraftForm
              parent={parent}
              onCancel={() => setCreating(false)}
              onCreate={async (draft) => {
                const goalId = `goal-${randomUUID()}`;
                const result = await mintWorldSubGoal({ worldKey, goalId, ...draft });
                if (!result.ok) {
                  data.setError(result.reason);
                  return false;
                }
                await data.refresh();
                notifyGoalsChanged();
                onChange([...value, goalId]);
                return true;
              }}
            />
          ) : null}
        </DialogPopup>
      </Dialog>
    </div>
  );
}
